import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "agent-action__header" }
const _hoisted_3 = { class: "agent-action__icon" }
const _hoisted_4 = { class: "agent-action__heading" }
const _hoisted_5 = { class: "agent-action__title" }
const _hoisted_6 = {
  key: 0,
  class: "agent-action__recipient"
}
const _hoisted_7 = {
  key: 0,
  class: "agent-action__muted"
}
const _hoisted_8 = {
  key: 1,
  class: "agent-action__recipient"
}
const _hoisted_9 = {
  class: "agent-action__status",
  role: "status"
}
const _hoisted_10 = {
  key: 0,
  class: "agent-action__edit"
}
const _hoisted_11 = { class: "agent-action__field" }
const _hoisted_12 = {
  key: 1,
  class: "agent-action__body"
}
const _hoisted_13 = { class: "agent-action__subject" }
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = {
  key: 0,
  class: "agent-action__message"
}
const _hoisted_16 = {
  key: 1,
  class: "agent-action__muted"
}
const _hoisted_17 = {
  key: 3,
  class: "agent-action__facts"
}
const _hoisted_18 = ["aria-expanded"]
const _hoisted_19 = {
  key: 2,
  class: "agent-action__reason"
}
const _hoisted_20 = {
  key: 3,
  class: "agent-action__outcome"
}
const _hoisted_21 = { class: "agent-action__buttons" }
const _hoisted_22 = {
  key: 5,
  class: "agent-action__footer"
}
const _hoisted_23 = { class: "agent-action__expiry" }
const _hoisted_24 = {
  key: 0,
  class: "agent-action__buttons"
}
const _hoisted_25 = {
  key: 1,
  class: "agent-action__buttons"
}

import { computed, reactive, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import DOMPurify from "dompurify";
import { Button, Icon, Input, RichTextEditor, Spinner, Textarea } from "@/components/ui";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import type { AgentAction, AgentActionType } from "@/dtos/Agent.dto";

const INVITE_NOTE_MAX = 200;
// Longer drafts start collapsed.
const LONG_TEXT_CHARS = 500;

export default /*@__PURE__*/_defineComponent({
  __name: 'AgentActionCard',
  props: {
    action: {},
    busy: { type: Boolean },
    now: {},
    title: {},
    approveLabel: {},
    rejectLabel: {},
    quickReject: { type: Boolean }
  },
  emits: ["approve", "reject"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Mirrors the backend tool's limit (send_linkedin_invite note).
const EMAIL_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const KINDS: Record<AgentActionType, { label: string; icon: string; verb: string; done: string }> =
  {
    send_email: { label: "Email", icon: "mdi:email-outline", verb: "send", done: "Sent" },
    send_linkedin_message: {
      label: "LinkedIn message",
      icon: "mdi:linkedin",
      verb: "send",
      done: "Sent",
    },
    send_linkedin_invite: {
      label: "LinkedIn invitation",
      icon: "mdi:account-plus-outline",
      verb: "send",
      done: "Invite sent",
    },
    book_meeting: {
      label: "Meeting",
      icon: "mdi:calendar-clock-outline",
      verb: "book",
      done: "Booked",
    },
    enroll_sequence: {
      label: "Enroll in sequence",
      icon: "mdi:playlist-play",
      verb: "enroll",
      done: "Enrolled",
    },
    create_campaign: {
      label: "Campaign",
      icon: "mdi:rocket-launch-outline",
      verb: "create",
      done: "Created",
    },
    bulk_update: {
      label: "Bulk update",
      icon: "mdi:pencil-box-multiple-outline",
      verb: "apply",
      done: "Applied",
    },
    spend_credits: {
      label: "Import prospects",
      icon: "mdi:database-import-outline",
      verb: "import",
      done: "Imported",
    },
  };

const kind = computed(
  () =>
    KINDS[props.action.actionType] ?? {
      label: "Action",
      icon: "mdi:robot-outline",
      verb: "run",
      done: "Done",
    }
);

/** What will actually go out: the rep's edit when there is one. */
const input = computed<Record<string, any>>(
  () => props.action.editedPayload ?? props.action.payload ?? {}
);

const recipientName = computed(() => {
  const c = props.action.contact;
  return c ? [c.fname, c.lname].filter(Boolean).join(" ") || c.email || "Unnamed contact" : "";
});

const recipientDetail = computed(() => {
  const c = props.action.contact;
  if (!c) return "";
  return props.action.actionType === "send_email" ? (c.email ?? "") : (c.jobTitle ?? "");
});

const contactCount = computed(() =>
  Array.isArray(input.value.contactIds) ? input.value.contactIds.length : 0
);

// Pending past its expiry: the server sweeps it within a minute, the card stops it now.
const isLapsed = computed(
  () =>
    props.action.status === "pending" &&
    !!props.action.expiresAt &&
    new Date(props.action.expiresAt).getTime() <= props.now
);
const isOpen = computed(() => props.action.status === "pending" && !isLapsed.value);

const status = computed(() => {
  if (isLapsed.value)
    return { tone: "muted", icon: "mdi:timer-off-outline", label: "Expired", busy: false };
  switch (props.action.status) {
    case "pending":
      return props.busy
        ? { tone: "busy", icon: "", label: "Working…", busy: true }
        : {
            tone: "pending",
            icon: "mdi:hand-back-right-outline",
            label: "Needs approval",
            busy: false,
          };
    case "approved":
    case "executing":
      return { tone: "busy", icon: "", label: "Sending…", busy: true };
    case "executed":
      return { tone: "success", icon: "mdi:check-circle", label: kind.value.done, busy: false };
    case "failed":
      return { tone: "danger", icon: "mdi:alert-circle", label: "Failed", busy: false };
    case "rejected":
      return { tone: "muted", icon: "mdi:close-circle-outline", label: "Rejected", busy: false };
    case "expired":
      return { tone: "muted", icon: "mdi:timer-off-outline", label: "Expired", busy: false };
    case "superseded":
      return { tone: "muted", icon: "mdi:skip-next-circle-outline", label: "Skipped", busy: false };
    default:
      return {
        tone: "muted",
        icon: "mdi:help-circle-outline",
        label: props.action.status,
        busy: false,
      };
  }
});

// Why it ended the way it did, in the server's words.
const outcome = computed(() => {
  const result = props.action.executionResult;
  switch (props.action.status) {
    case "failed":
      return result?.error || "It couldn't be sent.";
    case "superseded":
      return result?.reason || "Not sent: the contact replied or opted out first.";
    case "rejected":
      return props.action.rejectionReason ? `You said: ${props.action.rejectionReason}` : "";
    default:
      return "";
  }
});

const expiryText = computed(() => {
  if (!props.action.expiresAt) return "";
  const minutes = Math.round((new Date(props.action.expiresAt).getTime() - props.now) / 60_000);
  if (minutes < 60) return `Expires in ${Math.max(minutes, 1)} min`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `Expires in ${hours}h` : `Expires in ${Math.round(hours / 24)} days`;
});

const emailHtml = computed(() => DOMPurify.sanitize(String(input.value.body ?? "")));

const plainText = (html: string): string => {
  const el = document.createElement("div");
  el.innerHTML = DOMPurify.sanitize(html);
  return (el.textContent ?? "").trim();
};

const isLong = computed(() => {
  if (props.action.actionType === "send_email") {
    return plainText(String(input.value.body ?? "")).length > LONG_TEXT_CHARS;
  }
  if (props.action.actionType === "send_linkedin_message") {
    return String(input.value.text ?? "").length > LONG_TEXT_CHARS;
  }
  return false;
});
const expanded = ref(false);

const meetingTime = computed(() => {
  const start = input.value.startTime ? new Date(input.value.startTime) : null;
  if (!start || Number.isNaN(start.getTime())) return input.value.startTime ?? "";
  return start.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
});

const providerLabel = (provider: string): string =>
  ({ zoom: "Zoom", google: "Google Meet", teams: "Microsoft Teams" })[provider.toLowerCase()] ??
  provider;

// Only the words of a message are editable; who, when and which sequence stay as proposed.
const isEditable = computed(() =>
  ["send_email", "send_linkedin_message", "send_linkedin_invite"].includes(props.action.actionType)
);

const editing = ref(false);
const draft = reactive({ subject: "", body: "", text: "", note: "" });

const startEdit = () => {
  draft.subject = String(input.value.subject ?? "");
  draft.body = String(input.value.body ?? "");
  draft.text = String(input.value.text ?? "");
  draft.note = String(input.value.note ?? "");
  editing.value = true;
};

const cancelEdit = () => (editing.value = false);

const isDraftValid = computed(() => {
  switch (props.action.actionType) {
    case "send_email":
      return !!draft.subject.trim() && !!plainText(draft.body);
    case "send_linkedin_message":
      return !!draft.text.trim();
    case "send_linkedin_invite":
      return draft.note.length <= INVITE_NOTE_MAX;
    default:
      return true;
  }
});

const editedPayload = (): Record<string, unknown> => {
  const base = { ...input.value };
  switch (props.action.actionType) {
    case "send_email":
      return { ...base, subject: draft.subject.trim(), body: draft.body };
    case "send_linkedin_message":
      return { ...base, text: draft.text.trim() };
    case "send_linkedin_invite": {
      const note = draft.note.trim();
      if (note) return { ...base, note };
      delete base.note;
      return base;
    }
    default:
      return base;
  }
};

const saveAndApprove = () => {
  if (!isDraftValid.value) return;
  emit("approve", editedPayload());
};

const rejecting = ref(false);
const rejectReason = ref("");

const submitReject = () => {
  emit("reject", rejectReason.value.trim() || undefined);
};

// Once decided (here, in bulk or by the server), the edit and reject forms have nothing to do.
watch(isOpen, (open) => {
  if (!open) {
    editing.value = false;
    rejecting.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(["agent-action", `agent-action--${status.value.tone}`]),
    "aria-label": `${kind.value.label} ${recipientName.value ? `to ${recipientName.value}` : ''}`
  }, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, [
        _createVNode(_unref(Icon), {
          icon: kind.value.icon
        }, null, 8, ["icon"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("p", _hoisted_5, _toDisplayString(__props.title ?? kind.value.label), 1),
        (__props.action.contact)
          ? (_openBlock(), _createElementBlock("p", _hoisted_6, [
              _cache[9] || (_cache[9] = _createElementVNode("span", null, "to", -1)),
              _createVNode(_unref(RouterLink), {
                to: { name: _unref(CONTACT_OVERVIEW_ROUTE), params: { id: __props.action.contact.id } },
                class: "agent-action__contact"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(recipientName.value), 1)
                ]),
                _: 1
              }, 8, ["to"]),
              (recipientDetail.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_7, "· " + _toDisplayString(recipientDetail.value), 1))
                : _createCommentVNode("", true)
            ]))
          : (contactCount.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(contactCount.value) + " " + _toDisplayString(contactCount.value === 1 ? "contact" : "contacts"), 1))
            : _createCommentVNode("", true)
      ]),
      _createElementVNode("span", _hoisted_9, [
        (status.value.busy)
          ? (_openBlock(), _createBlock(_unref(Spinner), {
              key: 0,
              size: "xs"
            }))
          : (_openBlock(), _createBlock(_unref(Icon), {
              key: 1,
              icon: status.value.icon
            }, null, 8, ["icon"])),
        _createTextVNode(" " + _toDisplayString(status.value.label), 1)
      ])
    ]),
    (editing.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          (__props.action.actionType === 'send_email')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(Input), {
                  modelValue: draft.subject,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((draft.subject) = $event)),
                  size: "sm",
                  label: "Subject"
                }, null, 8, ["modelValue"]),
                _createElementVNode("div", _hoisted_11, [
                  _cache[10] || (_cache[10] = _createElementVNode("span", { class: "agent-action__label" }, "Message", -1)),
                  _createVNode(_unref(RichTextEditor), {
                    modelValue: draft.body,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((draft.body) = $event)),
                    height: "200px",
                    toolbar: EMAIL_TOOLBAR
                  }, null, 8, ["modelValue"])
                ])
              ], 64))
            : (__props.action.actionType === 'send_linkedin_message')
              ? (_openBlock(), _createBlock(_unref(Textarea), {
                  key: 1,
                  modelValue: draft.text,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draft.text) = $event)),
                  label: "Message",
                  rows: 6,
                  "max-length": 8000
                }, null, 8, ["modelValue"]))
              : (__props.action.actionType === 'send_linkedin_invite')
                ? (_openBlock(), _createBlock(_unref(Textarea), {
                    key: 2,
                    modelValue: draft.note,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((draft.note) = $event)),
                    label: "Invitation note (optional)",
                    rows: 3,
                    "max-length": INVITE_NOTE_MAX,
                    "show-char-count": ""
                  }, null, 8, ["modelValue"]))
                : _createCommentVNode("", true)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_12, [
          (__props.action.actionType === 'send_email')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("p", _hoisted_13, _toDisplayString(input.value.subject), 1),
                _createElementVNode("div", {
                  class: _normalizeClass(["agent-action__message agent-action__message--html", { 'agent-action__message--clamped': isLong.value && !expanded.value }]),
                  innerHTML: emailHtml.value
                }, null, 10, _hoisted_14)
              ], 64))
            : (__props.action.actionType === 'send_linkedin_message')
              ? (_openBlock(), _createElementBlock("p", {
                  key: 1,
                  class: _normalizeClass(["agent-action__message", { 'agent-action__message--clamped': isLong.value && !expanded.value }])
                }, _toDisplayString(input.value.text), 3))
              : (__props.action.actionType === 'send_linkedin_invite')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                    (input.value.note)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(input.value.note), 1))
                      : (_openBlock(), _createElementBlock("p", _hoisted_16, "No note: a plain connection request."))
                  ], 64))
                : (__props.action.actionType === 'book_meeting')
                  ? (_openBlock(), _createElementBlock("dl", _hoisted_17, [
                      _cache[12] || (_cache[12] = _createElementVNode("dt", null, "Title", -1)),
                      _createElementVNode("dd", null, _toDisplayString(input.value.title), 1),
                      _cache[13] || (_cache[13] = _createElementVNode("dt", null, "When", -1)),
                      _createElementVNode("dd", null, _toDisplayString(meetingTime.value), 1),
                      _cache[14] || (_cache[14] = _createElementVNode("dt", null, "Length", -1)),
                      _createElementVNode("dd", null, _toDisplayString(input.value.durationMinutes ?? 30) + " minutes", 1),
                      (input.value.provider || input.value.meetingUrl)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _cache[11] || (_cache[11] = _createElementVNode("dt", null, "Where", -1)),
                            _createElementVNode("dd", null, _toDisplayString(input.value.provider ? providerLabel(input.value.provider) : input.value.meetingUrl), 1)
                          ], 64))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
          (isLong.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 4,
                type: "button",
                class: "agent-action__more",
                "aria-expanded": expanded.value,
                onClick: _cache[4] || (_cache[4] = ($event: any) => (expanded.value = !expanded.value))
              }, _toDisplayString(expanded.value ? "Show less" : "Show more"), 9, _hoisted_18))
            : _createCommentVNode("", true)
        ])),
    (__props.action.reason)
      ? (_openBlock(), _createElementBlock("p", _hoisted_19, [
          _createVNode(_unref(Icon), { icon: "mdi:lightbulb-on-outline" }),
          _createElementVNode("span", null, _toDisplayString(__props.action.reason), 1)
        ]))
      : _createCommentVNode("", true),
    (outcome.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(outcome.value), 1))
      : _createCommentVNode("", true),
    (rejecting.value)
      ? (_openBlock(), _createElementBlock("form", {
          key: 4,
          class: "agent-action__reject",
          onSubmit: _withModifiers(submitReject, ["prevent"])
        }, [
          _createVNode(_unref(Input), {
            modelValue: rejectReason.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((rejectReason).value = $event)),
            size: "sm",
            placeholder: "Tell the assistant why (optional)",
            "aria-label": "Why you're rejecting this"
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_21, [
            _createVNode(_unref(Button), {
              variant: "ghost",
              size: "sm",
              disabled: __props.busy,
              onClick: _cache[6] || (_cache[6] = ($event: any) => (rejecting.value = false))
            }, {
              default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                _createTextVNode(" Cancel ", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(_unref(Button), {
              variant: "danger",
              size: "sm",
              type: "submit",
              loading: __props.busy
            }, {
              default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                _createTextVNode("Reject", -1)
              ]))]),
              _: 1
            }, 8, ["loading"])
          ])
        ], 32))
      : (isOpen.value)
        ? (_openBlock(), _createElementBlock("footer", _hoisted_22, [
            _createElementVNode("span", _hoisted_23, _toDisplayString(expiryText.value), 1),
            (editing.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                  _createVNode(_unref(Button), {
                    variant: "ghost",
                    size: "sm",
                    disabled: __props.busy,
                    onClick: cancelEdit
                  }, {
                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _createVNode(_unref(Button), {
                    variant: "primary",
                    size: "sm",
                    loading: __props.busy,
                    disabled: !isDraftValid.value,
                    onClick: saveAndApprove
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Save & " + _toDisplayString(kind.value.verb), 1)
                    ]),
                    _: 1
                  }, 8, ["loading", "disabled"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_25, [
                  _createVNode(_unref(Button), {
                    variant: "ghost",
                    size: "sm",
                    disabled: __props.busy,
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (__props.quickReject ? emit('reject') : (rejecting.value = true)))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(__props.rejectLabel ?? "Reject"), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"]),
                  _renderSlot(_ctx.$slots, "actions", { busy: __props.busy }),
                  (isEditable.value)
                    ? (_openBlock(), _createBlock(_unref(Button), {
                        key: 0,
                        variant: "outline",
                        size: "sm",
                        disabled: __props.busy,
                        onClick: startEdit
                      }, {
                        default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                          _createTextVNode(" Edit ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(Button), {
                    variant: "primary",
                    size: "sm",
                    loading: __props.busy,
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (emit('approve')))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(__props.approveLabel ?? `Approve & ${kind.value.verb}`), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ]))
          ]))
        : _createCommentVNode("", true)
  ], 10, _hoisted_1))
}
}

})